import { useParams, Link, Navigate } from "react-router-dom";
import { useGenerationStatus, isGenerationAllowed } from "@/hooks/use-generation";
import { CheckCircle2, XCircle, ArrowLeft, ArrowRight, RotateCcw, Sparkles, Hexagon, Download, Loader2, Lock, FastForward, Zap, Crown, Film, Gauge } from "lucide-react";
import { calculateProgress, calculateTimeRemaining, formatTime, inferClipSeconds } from "@/lib/generation-utils";
import { useState, useEffect, useCallback } from "react";
import { useUserStore } from "@/stores/user-store";

const TIPS = [
  "Describe hair color, eye style, and outfit details",
  "Try specific anime styles like 'chibi' or 'shonen'",
  "Add accessories like ribbons, cat ears, or weapons",
  "Mention expressions and poses for more personality",
  "Include background details for richer scenes",
];

export function GeneratePage() {
  const { id } = useParams<{ id: string }>();
  const allowed = id ? isGenerationAllowed(id) : false;
  const { data } = useGenerationStatus(allowed ? (id ?? null) : null);
  const generation = data?.generation;
  const { hasPurchased, credits } = useUserStore();
  const [fallbackStart] = useState(() => Date.now());
  const [, setTick] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (!allowed || generation?.status === "completed" || generation?.status === "failed") return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [allowed, generation?.status]);

  useEffect(() => {
    if (!allowed || generation?.status === "completed" || generation?.status === "failed") return;
    const interval = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 4000);
    return () => clearInterval(interval);
  }, [allowed, generation?.status]);

  if (!id || !allowed) {
    return <Navigate to="/" replace />;
  }

  const clipSeconds = inferClipSeconds(
    generation?.model_id,
    generation?.credits_used,
    generation?.duration_ms,
  );
  const startTime = generation?.created_at
    ? new Date(generation.created_at).getTime()
    : fallbackStart;
  const progress = calculateProgress(startTime, generation?.model_id, clipSeconds);
  const timeRemaining = calculateTimeRemaining(startTime, generation?.model_id, clipSeconds);

  const MODEL_ROUTES: Record<string, string> = {
    qwen_image: "/create-character",
    qwen_image_edit: "/create-character",
    cs_t2v_480p_5s: "/animate-character",
    cs_480p_i2v_5s: "/animate-character",
    cs_480p_i2v_8s: "/animate-character",
    cs_720p_i2v_5s: "/animate-character",
    cs_720p_i2v_8s: "/animate-character",
    wan25_720p_i2v_5s: "/animate-character",
    integrated_h3_i2v: "/animate-character",
  };
  const createRoute = MODEL_ROUTES[generation?.model_id || ""] || "/create-character";

  return (
    <div className="p-5 md:p-8 max-w-3xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors mb-8 font-semibold">
        <ArrowLeft className="size-3.5" /> Home
      </Link>

      {(!generation || generation.status === "pending" || generation.status === "processing") && (
        <div className="flex flex-col items-center justify-center py-12 animate-fade-in-up">
          <div className="relative mb-10">
            <div className="absolute inset-0 rounded-full bg-[#d4a574]/15 blur-[40px] animate-pulse-glow" style={{ width: 120, height: 120, margin: "-10px" }} />
            <div className="relative size-24 rounded-full bg-[#d4a574]/10 border border-[#d4a574]/20 flex items-center justify-center animate-pulse-ring">
              <div className="size-14 rounded-full bg-[#d4a574]/8 border border-[#d4a574]/15 flex items-center justify-center">
                <Sparkles className="size-6 text-[#d4a574] animate-float" />
              </div>
            </div>
          </div>
          <h2 className="text-xl font-extrabold text-foreground mb-1 tracking-tight">Bringing your character to life~</h2>
          <p className="text-sm text-muted-foreground mb-2">
            {timeRemaining !== null ? `~${formatTime(timeRemaining)} remaining` : "This usually takes about a minute..."}
          </p>
          <p className="text-[11px] text-[#d4a574] mb-8 h-4 transition-opacity">
            <Hexagon className="size-2.5 inline mr-1 relative -top-px" />
            {TIPS[tipIndex]}
          </p>
          <div className="w-full max-w-xs">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-[#d4a574] transition-all duration-1000 ease-out" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between mt-2">
              <p className="text-[10px] text-muted-foreground tabular-nums">{Math.round(progress)}%</p>
              <p className="text-[10px] text-muted-foreground">{generation?.status === "pending" ? "Queued" : "Processing"}</p>
            </div>
          </div>

          {!hasPurchased && (
            <Link
              to="/billing"
              className="mt-10 w-full max-w-sm p-4 rounded-2xl card-glass border border-[#d4a574]/20 hover:border-[#d4a574]/40 transition-all group block"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="size-8 rounded-xl bg-[#d4a574]/10 border border-[#d4a574]/20 flex items-center justify-center">
                  <Crown className="size-4 text-[#d4a574]" />
                </div>
                <p className="text-[13px] font-bold text-foreground">Upgrade Your Creations</p>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center gap-1.5">
                  <Lock className="size-3 text-[#d4a574]/70" />
                  <span className="text-[11px] text-muted-foreground">No watermarks</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="size-3 text-[#d4a574]/70" />
                  <span className="text-[11px] text-muted-foreground">Better quality models</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Film className="size-3 text-[#d4a574]/70" />
                  <span className="text-[11px] text-muted-foreground">Longer generations</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Gauge className="size-3 text-[#d4a574]/70" />
                  <span className="text-[11px] text-muted-foreground">Faster generation times</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-[#d4a574]/10 border border-[#d4a574]/20 group-hover:bg-[#d4a574]/15 transition-colors">
                <Sparkles className="size-3.5 text-[#d4a574]" />
                <span className="text-[12px] font-bold text-[#d4a574]">Buy Credits</span>
              </div>
            </Link>
          )}
        </div>
      )}

      {generation?.status === "completed" && generation.url && (() => {
        const isImage = generation.model_id === "qwen_image" || generation.model_id === "qwen_image_edit";
        const displayUrl = hasPurchased ? generation.url : (generation.watermarked_url || generation.url);
        // Only send a rerun back to the create form if the balance actually covers
        // it; otherwise the pack page is the honest destination.
        const canAffordRerun = credits > 0 && credits >= (generation.credits_used ?? 0);
        return (
        <div className="animate-fade-in-up">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 mb-6 bg-black flex items-center justify-center">
            {isImage ? (
              <img src={displayUrl} alt="Generation result" className="w-full max-h-[70vh] object-contain" />
            ) : (
              <video src={displayUrl} controls autoPlay loop playsInline className="w-full max-h-[70vh] object-contain" />
            )}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm border border-emerald-500/20">
              <CheckCircle2 className="size-3 text-emerald-400" />
              <span className="text-[10px] font-bold text-emerald-400">Complete</span>
            </div>
          </div>

          {!isImage && (
            <Link
              to={hasPurchased ? `/extend-video?video_id=${generation.id}` : "/billing?src=post_gen_extend"}
              className="mb-4 block w-full p-4 rounded-2xl bg-gradient-to-r from-[#d4a574]/25 to-[#d4a574]/8 border-2 border-[#d4a574]/50 hover:border-[#d4a574]/70 hover:from-[#d4a574]/30 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-2xl bg-[#d4a574]/20 border border-[#d4a574]/40 flex items-center justify-center shrink-0">
                  <FastForward className="size-5 text-[#d4a574]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-extrabold text-foreground tracking-tight">
                    🎬 Add 5–15 seconds more now
                  </p>
                  <p className="text-[12px] text-muted-foreground mt-0.5 leading-snug">
                    {hasPurchased
                      ? "Continue this scene where it left off — new motion, dialog, or a fresh beat."
                      : "Get credits to extend this video with more action, dialog, or a new scene."}
                  </p>
                </div>
                <ArrowRight className="size-4 text-[#d4a574] shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          )}

          {!hasPurchased ? (
            <>
              <p className="text-sm text-foreground/80 text-center mb-4 leading-relaxed">
                Like this quality? Unlock <span className="font-semibold">Power</span> and more generations with any pack.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Link
                  to="/billing?src=post_gen_watermark"
                  className="btn-primary w-full py-3 rounded-xl flex items-center justify-center gap-2 text-[13px]"
                >
                  <Lock className="size-4" /> Remove Watermark
                </Link>
                <Link
                  to={canAffordRerun ? createRoute : "/billing?src=post_gen_regen"}
                  className="btn-glass w-full py-3 rounded-xl flex items-center justify-center gap-2 text-[13px] border-[#d4a574]/20 hover:border-[#d4a574]/40"
                >
                  <RotateCcw className="size-4 text-[#d4a574]" /> Generate Again
                </Link>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                {generation.download_url && (
                  <DownloadButton url={generation.download_url} isImage={isImage} label="Download Preview" />
                )}
                <span className="text-[10px] text-muted-foreground">{generation.credits_used} gizmos used</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <Link to={createRoute} className="btn-primary flex items-center gap-2 text-[13px] py-2.5 px-5">
                <Sparkles className="size-3.5" /> Create Another
              </Link>
              {generation.download_url && <DownloadButton url={generation.download_url} isImage={isImage} />}
              <span className="text-[10px] text-muted-foreground">{generation.credits_used} gizmos used</span>
            </div>
          )}

          {hasPurchased && (
            <div className="mt-6 text-center">
              <p className="text-[13px] text-muted-foreground">
                Unhappy with a generation? <a href="mailto:support@animepup.com" className="text-[#d4a574] font-semibold hover:underline">Send us an email</a> and we'll help you out :)
              </p>
            </div>
          )}

          {generation.credits_used === 0 && generation.model_id === "integrated_h3_i2v" && (
            <Link
              to="/animate-character"
              className="mt-4 block w-full p-4 rounded-2xl bg-gradient-to-r from-[#d4a574]/15 to-[#d4a574]/5 border-2 border-[#d4a574]/20 hover:border-[#d4a574]/40 transition-all"
            >
              <p className="text-[15px] font-extrabold text-foreground tracking-tight mb-1">
                Like what you see?
              </p>
              <p className="text-[13px] text-muted-foreground leading-relaxed">
                Create more videos like this with <span className="font-bold text-[#d4a574]">Power</span> on the Animate page. Same upgraded pipeline, same quality.
              </p>
              <div className="flex items-center gap-2 mt-3 text-[13px] font-bold text-[#d4a574]">
                <Sparkles className="size-4" />
                Go to Animate
                <ArrowRight className="size-3.5" />
              </div>
            </Link>
          )}
        </div>
        );
      })()}

      {generation?.status === "failed" && (
        <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up">
          <div className="size-14 rounded-full bg-red-50 border border-red-200/40 flex items-center justify-center mb-4">
            <XCircle className="size-6 text-red-400" />
          </div>
          <h2 className="text-base font-bold text-foreground mb-1">Generation Failed</h2>
          <p className="text-xs text-muted-foreground mb-1 text-center max-w-sm">{generation.error_message || "Something went wrong"}</p>
          <p className="text-[11px] text-emerald-500 mb-5">Gizmos have been refunded</p>
          <Link to={createRoute} className="btn-primary inline-flex items-center gap-2 text-[13px]">
            <RotateCcw className="size-3.5" /> Try Again
          </Link>
        </div>
      )}
    </div>
  );
}

function DownloadButton({ url, isImage, label = "Download" }: { url: string; isImage: boolean; label?: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const ext = isImage ? "png" : "mp4";
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `animepup-generation.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    } finally {
      setDownloading(false);
    }
  }, [url, isImage]);

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="btn-glass flex items-center gap-2 text-[13px] py-2.5 px-5 rounded-xl"
    >
      {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
      {label}
    </button>
  );
}
